import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Card, CardBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronRight,
  faTools,
} from '@fortawesome/free-solid-svg-icons';

import {
  ReminderInstanceCalendarType,
  ReminderInstanceSignalType,
} from '../ProgressBars';
import { MAINTENANCE_NOTIFICATION_STATUSES } from '../../Constants/GatewayReminderInstances';
import {
  ALL_TYPE,
  CALENDAR_TYPE,
  GTEMPLATE_REMINDER_TYPES,
  SIGNAL_TYPE,
} from '../../Constants/GtemplateReminder';

import GatewayInstanceMobileMaintenanceCardDetails from './GatewayInstanceMobileMaintenanceCardDetails';

export default function GatewayInstanceMobileMaintenanceCard(props) {
  const { checkedInstances, handleInstanceChecked, instance } = props,
    isCompletedOneTimeReminder = useMemo(
      () =>
        !instance.GtemplateReminder?.recurring &&
        !!instance.MaintenanceHistoryLogs?.length,
      [
        instance.GtemplateReminder?.recurring,
        instance.MaintenanceHistoryLogs?.length,
      ]
    ),
    clickHandler = useCallback(() => {
      if (isCompletedOneTimeReminder) return;
      handleInstanceChecked([instance?._id, instance]);
    }, [handleInstanceChecked, instance, isCompletedOneTimeReminder]),
    isChecked = useMemo(
      () => checkedInstances.has(instance._id),
      [checkedInstances, instance]
    ),
    [showCardDetails, toggleCardDetails] = useState(false);
  const { display_precision, unit } =
      props.signalUnitMap.get(instance.GtemplateReminder?.signal_id) || {},
    {
      calendar_end,
      calendar_percentage,
      calendar_progress,
      calendar_text,
      calendar_upcoming,
      signal_current,
      signal_end,
      signal_margin,
      signal_percentage,
      signal_progress,
      signal_text,
    } = props.instanceShadowValueMap.get(instance._id),
    [{ signal_value_when_serviced } = {}] =
      instance.MaintenanceHistoryLogs ?? [];
  return (
    <Card
      className={`rounded-lg p-2 ${
        isChecked ? 'border border-brand-primary-alert' : ''
      }`}
    >
      <CardBody className="p-0 d-flex flex-nowrap align-items-center text-muted">
        <div
          aria-disabled={isCompletedOneTimeReminder}
          className="flex-fill"
          onClick={clickHandler}
          onKeyPress={clickHandler}
          role="button"
          tabIndex={0}
        >
          <div className="d-flex align-items-center py-2">
            <FontAwesomeIcon
              className="text-brand-primary"
              icon={isChecked ? faCheck : faTools}
              size="1x"
            />
            <p className="font-weight-bolder flex-wrap mb-0 ml-2">
              {instance?.GtemplateReminder?.name ?? ''}
            </p>
          </div>
          <hr className="my-2" />
          <div className="d-flex flex-column">
            {(() => {
              switch (instance.GtemplateReminder.type) {
                case ALL_TYPE:
                  return (
                    <>
                      <div className="mt-2 mb-1">
                        <ReminderInstanceCalendarType
                          calendar_end={calendar_end}
                          calendar_percentage={calendar_percentage}
                          calendar_upcoming={calendar_upcoming}
                          instance={instance}
                          isCompletedOneTimeReminder={
                            isCompletedOneTimeReminder
                          }
                          progress_color={calendar_progress}
                          text={calendar_text}
                        />
                      </div>
                      <div className="mt-1">
                        <ReminderInstanceSignalType
                          display_precision={display_precision}
                          instance={instance}
                          isCompletedOneTimeReminder={
                            isCompletedOneTimeReminder
                          }
                          progress_color={signal_progress}
                          signal_current={signal_current}
                          signal_end={signal_end}
                          signal_margin={signal_margin}
                          signal_percentage={signal_percentage}
                          signal_value_when_serviced={
                            signal_value_when_serviced
                          }
                          text={signal_text}
                          unit={unit}
                        />
                      </div>
                    </>
                  );
                case CALENDAR_TYPE:
                  return (
                    <ReminderInstanceCalendarType
                      calendar_end={calendar_end}
                      calendar_percentage={calendar_percentage}
                      calendar_upcoming={calendar_upcoming}
                      instance={instance}
                      isCompletedOneTimeReminder={isCompletedOneTimeReminder}
                      progress_color={calendar_progress}
                      text={calendar_text}
                    />
                  );
                case SIGNAL_TYPE:
                  return (
                    <ReminderInstanceSignalType
                      instance={instance}
                      isCompletedOneTimeReminder={isCompletedOneTimeReminder}
                      progress_color={signal_progress}
                      signal_current={signal_current}
                      signal_end={signal_end}
                      signal_margin={signal_margin}
                      signal_percentage={signal_percentage}
                      signal_value_when_serviced={signal_value_when_serviced}
                      text={signal_text}
                      unit={unit}
                    />
                  );
                default:
                  return null;
              }
            })()}
          </div>
        </div>
        <div className="ml-2 d-flex align-items-center justify-content-center">
          <button
            className="btn btn-link p-0 text-decoration-none shadow-none"
            onClick={() => toggleCardDetails(!showCardDetails)}
          >
            <FontAwesomeIcon
              className="text-muted"
              icon={faChevronRight}
              size="lg"
            />
          </button>
        </div>
      </CardBody>
      {showCardDetails ? (
        <GatewayInstanceMobileMaintenanceCardDetails
          {...props}
          isOpen={showCardDetails}
          toggleModal={() => toggleCardDetails(!showCardDetails)}
        />
      ) : null}
      <style jsx>{`
        div:active,
        div:focus {
          outline: 0;
          border: none;
          -moz-outline-style: none;
        }
      `}</style>
      <style jsx global>{`
        .border-brand-primary-alert {
          border-width: medium !important;
        }
      `}</style>
    </Card>
  );
}

GatewayInstanceMobileMaintenanceCard.propTypes = {
  checkedInstances: PropTypes.object,
  handleInstanceChecked: PropTypes.func,
  instance: PropTypes.shape({
    _id: PropTypes.string,
    GtemplateReminder: PropTypes.shape({
      attachment: PropTypes.string,
      message: PropTypes.string,
      name: PropTypes.string,
      recurring: PropTypes.bool,
      signal_id: PropTypes.string,
      trigger_conditions: PropTypes.shape({
        calendar_trigger: PropTypes.shape({
          interval: PropTypes.string,
          margin: PropTypes.string,
        }),
        signal_trigger: PropTypes.shape({
          interval: PropTypes.number,
          margin: PropTypes.number,
        }),
      }),
      type: PropTypes.oneOf(GTEMPLATE_REMINDER_TYPES),
    }),
    MaintenanceHistoryLogs: PropTypes.arrayOf(PropTypes.object),
    MaintenanceNotifications: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        maintenance_completed: PropTypes.bool,
        signal_value: PropTypes.string,
        status: PropTypes.oneOf(MAINTENANCE_NOTIFICATION_STATUSES),
        MaintenanceHistoryLog: PropTypes.shape({
          contact_info: PropTypes.string,
          message: PropTypes.string,
          service_date: PropTypes.string,
          signal_value_when_serviced: PropTypes.string,
          user_id: PropTypes.string,
        }),
      })
    ),
    next_signal_value_due: PropTypes.string,
    next_time_due: PropTypes.string,
  }),
  instanceShadowValueMap: PropTypes.object,
  signalUnitMap: PropTypes.object,
};
